import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import TrialBanner from "../components/TrialBanner";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Users, Bell, BarChart3, CheckCircle2, Info, Calendar } from "lucide-react";

export default function AgencyOverview() {
  const [roster, setRoster] = useState([]);
  const [sos, setSos] = useState([]);
  const [hmde, setHmde] = useState(null);
  const [completion, setCompletion] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    api.get("/agency/roster").then(r => setRoster(r.data)).catch(() => {});
    api.get("/agency/sos-signals").then(r => setSos(r.data)).catch(() => {});
    api.get("/agency/hmde-aggregate").then(r => setHmde(r.data)).catch(() => {});
    api.get("/agency/completion").then(r => setCompletion(r.data)).catch(() => {});
    api.get("/agency/scheduler/status").then(r => setSchedulerStatus(r.data)).catch(() => {});
  }, []);

  const completed = completion.reduce((acc, m) => {
    return acc + Object.values(m.modules).filter(mod => mod.completed_at).length;
  }, 0);

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <TrialBanner />
        <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Agency Console · Training Platform
        </div>
        <h1 className="font-heading text-3xl font-bold">Overview</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You see participation data only. You never see scenario content, individual scores, or any conversation from an SOS / EAP session.
        </p>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Roster" value={roster.length} test="stat-roster" />
          <Stat icon={CheckCircle2} label="Completions" value={completed} test="stat-completions" />
          <Stat icon={Bell} label="SOS signals (lifetime)" value={sos.length} test="stat-sos" />
          <Stat icon={BarChart3} label="Cohort size" value={hmde?.cohort_size ?? 0} test="stat-cohort" />
        </div>

        <Card className="mt-10">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Aggregate HMDE distribution (last 30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {hmde && !hmde.available ? (
              <div className="flex items-start gap-3 rounded-md border border-border p-4 text-sm">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  Aggregate HMDE metrics require a minimum cohort of <strong>{hmde.min_cohort_required}</strong> service members.
                  Current cohort: <strong>{hmde.cohort_size}</strong>. This protects individual confidentiality.
                </div>
              </div>
            ) : hmde?.available ? (
              <ZoneBars dist={hmde.distribution} trends={hmde.trends} />
            ) : (
              <div className="text-sm text-muted-foreground">Loading…</div>
            )}
          </CardContent>
        </Card>

        {/* Scheduler status — annual auto-enrollment */}
        {schedulerStatus && (
          <Card data-testid="scheduler-status-card" className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-base">Annual auto-enrollment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                  <span className="text-muted-foreground">Next run</span>
                  <span className="font-semibold tabular-nums">
                    {schedulerStatus.next_run_at?.slice(0, 16).replace("T", " ") || "—"} UTC
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Last enrolled</span>
                  <span className="font-semibold tabular-nums">
                    {schedulerStatus.recent_runs?.[0]?.enrolled_count ?? 0}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {schedulerStatus.recent_runs?.[0]?.ran_at?.slice(0, 16).replace("T", " ") || "never"}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Members are auto-enrolled 30 days before in-service anniversary.
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}

const Stat = ({ icon: Icon, label, value, test }) => (
  <Card data-testid={test}>
    <CardContent className="flex items-center gap-3 p-5">
      <div className="rounded-md p-2" style={{ background: "hsl(var(--secondary))" }}>
        <Icon className="h-4 w-4" style={{ color: "hsl(var(--haruki-teal))" }} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-heading text-2xl font-semibold">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const ZoneBars = ({ dist, trends }) => {
  const total = Math.max(dist.total, 1);
  const rows = [
    { key: "resilience", label: "Resilience Zone", color: "hsl(var(--haruki-leaf))" },
    { key: "neutral",    label: "Neutral Zone",    color: "hsl(var(--haruki-neutral))" },
    { key: "fire",       label: "Emotional Fire Zone", color: "hsl(var(--haruki-fire))" },
  ];
  return (
    <div className="space-y-4">
      {rows.map(r => {
        const v = dist[r.key] || 0;
        const pct = Math.round((v / total) * 100);
        return (
          <div key={r.key}>
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-semibold uppercase tracking-widest" style={{ color: r.color }}>{r.label}</span>
              <span className="text-muted-foreground">{v} response{v === 1 ? "" : "s"} ({pct}%)</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: r.color }} />
            </div>
          </div>
        );
      })}
      <div className="grid gap-3 pt-4 sm:grid-cols-3">
        {["30d", "90d", "365d"].map(w => (
          <div key={w} className="rounded-md border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{w} Fire trend</div>
            <div className="mt-1 text-sm font-semibold capitalize">{trends?.[w]?.direction || "stable"}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
